import React, { useEffect, useState } from "react";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { money } from "@/context/CartContext";

/* Compact banner surfacing 2 random Shopify products → internal /shop/<handle>. Selection fixed per mount. */
export default function ShopPromoBanner({ testidPrefix = "shop-promo", className = "" }) {
    const { t, lang } = useLang();
    const [products, setProducts] = useState([]);

    useEffect(() => {
        let alive = true;
        api.get("/shop/catalog").then((r) => {
            if (!alive) return;
            const arr = (r.data || []).filter((p) => p.featuredImage?.url);
            setProducts([...arr].sort(() => Math.random() - 0.5).slice(0, 2));
        }).catch(() => { if (alive) setProducts([]); });
        return () => { alive = false; };
    }, []);

    if (products.length === 0) return null;

    return (
        <section data-testid={testidPrefix} className={`bg-black border border-abnormal-acid/40 ${className}`}>
            <div className="grid md:grid-cols-[1fr_auto] items-stretch">
                <div className="p-5 md:p-7">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-2 inline-flex items-center gap-2">
                        <ShoppingBag size={12} /> {t("shopPromo.eyebrow")}
                    </p>
                    <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">{t("shopPromo.title")}</h3>
                    <p className="text-zinc-400 text-sm mt-2 max-w-md">{t("shopPromo.sub")}</p>
                    <Link to="/shop" data-testid={`${testidPrefix}-cta`}
                        className="mt-4 inline-flex items-center gap-2 bg-abnormal-acid text-black px-5 py-2.5 text-xs uppercase tracking-[0.25em] font-bold hover:bg-abnormal-paper hover:text-black transition-colors">
                        {t("shopPromo.cta")} <ArrowRight size={13} />
                    </Link>
                </div>
                <div className="grid grid-cols-2 border-t md:border-t-0 md:border-l border-white/10">
                    {products.map((p) => (
                        <Link key={p.handle} to={`/shop/${p.handle}`} data-testid={`${testidPrefix}-product-${p.handle}`}
                            className="group relative aspect-square w-32 md:w-40 lg:w-44 overflow-hidden bg-zinc-950 border-r last:border-r-0 border-white/10">
                            <img src={p.featuredImage.url} alt={p.featuredImage.altText || p.title} loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-2">
                                <p className="text-white text-[10px] uppercase tracking-wider font-bold line-clamp-1">{p.title}</p>
                                <p className="text-abnormal-acid font-display text-sm">{money(p.priceRange?.minVariantPrice, lang)}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
